import { cyrb128, sfc32 } from "./sfc32.js";

/**
 * Random Number Generator class
 *
 * Provides high-level API for seeded random operations using the
 * sfc32 PRNG algorithm. This class makes it easy to generate
 * deterministic random numbers, integers, floats, and perform
 * operations like shuffling with a consistent seed.
 *
 * Why use seeded RNG?
 * - Reproducibility: Same seed = same sequence
 * - Testing: Predictable outcomes for debugging
 * - Daily Challenges: All players get same sequence on same day
 * - Fair Competition: Identical starting conditions
 *
 * @example
 * ```ts
 * // Create RNG with specific seed
 * const rng = new RNG("2025-01-24");
 *
 * // Generate random numbers
 * const float = rng.next();        // [0, 1)
 * const int = rng.int(1, 10);      // [1, 10]
 * const float2 = rng.float(0, 100); // [0, 100)
 *
 * // Random chance
 * if (rng.chance(0.3)) {
 *   console.log("30% chance occurred!");
 * }
 *
 * // Shuffle array
 * const cards = ['A', 'K', 'Q', 'J'];
 * rng.shuffle(cards);
 * ```
 */
export class RNG {
  private rng: () => number;

  /**
   * Create a new RNG instance with a seed
   *
   * @param seed - String seed (will be hashed) or omit for time-based seed
   *
   * @example
   * ```ts
   * // Deterministic RNG (same seed = same sequence)
   * const dailyRNG = new RNG("2025-01-24");
   *
   * // Random RNG (different each time)
   * const randomRNG = new RNG();
   * ```
   */
  constructor(seed?: string) {
    const seedStr = seed || Date.now().toString();
    const seeds = cyrb128(seedStr);
    this.rng = sfc32(seeds[0], seeds[1], seeds[2], seeds[3]);
  }

  /**
   * Generate random float in range [0, 1)
   *
   * @returns Random number between 0 (inclusive) and 1 (exclusive)
   *
   * @example
   * ```ts
   * const rng = new RNG("seed");
   * const value = rng.next(); // 0.123456...
   * ```
   */
  next(): number {
    return this.rng();
  }

  /**
   * Generate random integer in range [min, max] (inclusive)
   *
   * @param min - Minimum value (inclusive)
   * @param max - Maximum value (inclusive)
   * @returns Random integer
   *
   * @example
   * ```ts
   * const rng = new RNG("seed");
   * const dice = rng.int(1, 6);      // Roll d6: 1-6
   * const damage = rng.int(10, 20);  // 10-20 damage
   * ```
   */
  int(min: number, max: number): number {
    return Math.floor(this.rng() * (max - min + 1)) + min;
  }

  /**
   * Generate random float in range [min, max)
   *
   * @param min - Minimum value (inclusive)
   * @param max - Maximum value (exclusive)
   * @returns Random float
   *
   * @example
   * ```ts
   * const rng = new RNG("seed");
   * const speed = rng.float(1.0, 2.0);     // 1.0-2.0 speed multiplier
   * const angle = rng.float(0, Math.PI);   // Random angle
   * ```
   */
  float(min: number, max: number): number {
    return this.rng() * (max - min) + min;
  }

  /**
   * Random boolean with given probability
   *
   * @param probability - Probability of true (0-1)
   * @returns true or false
   *
   * @example
   * ```ts
   * const rng = new RNG("seed");
   *
   * // 30% chance of critical hit
   * if (rng.chance(0.3)) {
   *   console.log("Critical hit!");
   * }
   *
   * // 50% chance (coin flip)
   * const heads = rng.chance(0.5);
   * ```
   */
  chance(probability: number): boolean {
    return this.rng() < probability;
  }

  /**
   * Fisher-Yates shuffle with seeded RNG
   *
   * Produces deterministic shuffle for same seed.
   * Modifies array in place and returns it.
   *
   * @param array - Array to shuffle (will be modified)
   * @returns Shuffled array (same reference)
   *
   * @example
   * ```ts
   * const rng = new RNG("2025-01-24");
   * const deck = ['A', 'K', 'Q', 'J', '10', '9'];
   * rng.shuffle(deck);
   * console.log(deck); // Shuffled: ['Q', '9', 'A', '10', 'K', 'J']
   *
   * // Same seed = same shuffle
   * const rng2 = new RNG("2025-01-24");
   * const deck2 = ['A', 'K', 'Q', 'J', '10', '9'];
   * rng2.shuffle(deck2);
   * console.log(deck2); // Same: ['Q', '9', 'A', '10', 'K', 'J']
   * ```
   */
  shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      const temp = array[i]!;
      array[i] = array[j]!;
      array[j] = temp;
    }
    return array;
  }
}

/**
 * Global RNG instance for general game use
 * Uses time-based seed for non-deterministic behavior
 *
 * @internal
 */
const globalRNG = new RNG();

/**
 * Quick access function for random number generation
 *
 * Returns a random number in range [0, 1) using a shared global RNG instance.
 * This is useful for non-critical randomness where reproducibility isn't needed.
 *
 * For deterministic behavior (e.g., daily challenges), create a seeded RNG instance instead.
 *
 * @returns Random number between 0 (inclusive) and 1 (exclusive)
 *
 * @example
 * ```ts
 * import { rng } from '@paulie/game-utils';
 *
 * // Quick random checks
 * if (rng() < 0.5) {
 *   console.log("50% chance occurred!");
 * }
 *
 * // Random positioning
 * const x = 10 + rng() * 80; // 10-90% range
 * ```
 */
export function rng(): number {
  return globalRNG.next();
}
